import {GetPageResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/story_page/v1/service.types';
import {SPACING} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useCallback} from 'react';
import {StreamTileListProps} from '../../../features/ProductStream/StreamTileList';
import {ProductTile} from '../../../features/ProductTile/ProductTile';
import {StreamProductTileWrapper} from '../../../features/ProductTile/StreamProductTileWrapper';
import {StoryCtaButton} from '../ContentBlocks/StoryCtaButton';
import {StoryDoubleTile} from '../ContentBlocks/StoryDoubleTile';
import {StoryHeadline} from '../ContentBlocks/StoryHeadline';
import {StoryImageText} from '../ContentBlocks/StoryImageText';
import {StoryImageTile} from '../ContentBlocks/StoryImageTile';
import {StoryOutfit} from '../ContentBlocks/StoryOutfit';
import {StoryText} from '../ContentBlocks/StoryText';
import {StoryVideoTile} from '../ContentBlocks/StoryVideoTile';

export const StoryStreamTileList: React.FC<
  Pick<GetPageResponse, 'items'> & StreamTileListProps
> = ({
  items,
  gridColumns,
  onClickAddCta,
  addCtaTitle,
  ProductTileComponent = ProductTile,
  shallowRenderingState,
}) => {
  const handleClickAddCta = useCallback(
    ({
        productId,
        filteredSizeId,
        couponPromotionId,
        recoType,
        streamPosition,
        locationId,
      }: {
        productId: number;
        filteredSizeId?: number;
        couponPromotionId?: string;
        recoType?: string;
        streamPosition?: number;
        locationId?: string;
      }) =>
      () => {
        onClickAddCta?.({
          productId,
          filteredSizeId,
          couponPromotionId,
          recoType,
          streamPosition,
          locationId,
        });
      },
    [onClickAddCta],
  );

  return (
    <>
      {items.map((item, index) => {
        const streamPosition = 'position' in item ? item.position : index;

        switch (item.type?.$case) {
          case 'productSection': {
            const {productTile, sectionTracker} = item.type.productSection;
            const productId = productTile.productId;

            return (
              <StreamProductTileWrapper
                key={`productSection-${productId}`}
                productId={productId}
                shallowRenderingState={shallowRenderingState}
                renderTile={({isInViewOnRemount, tileRef}) => (
                  <ProductTileComponent
                    isInViewOnRemount={isInViewOnRemount}
                    tileRef={tileRef as React.RefObject<HTMLDivElement | null>}
                    tile={productTile}
                    streamPosition={streamPosition}
                    gridColumns={gridColumns}
                    addCtaLabel={addCtaTitle}
                    trackingData={{recoType: sectionTracker?.recoType}}
                    onClickAddCta={
                      onClickAddCta
                        ? handleClickAddCta({
                            productId,
                            filteredSizeId: productTile.selectedSizeId,
                            couponPromotionId: productTile.couponPromotionId,
                            recoType: sectionTracker?.recoType,
                            streamPosition,
                            locationId: sectionTracker?.recoType,
                          })
                        : undefined
                    }
                  />
                )}
              />
            );
          }
          case 'headline':
            return (
              <ContentBlockRow key={`headline-${streamPosition}`}>
                <StoryHeadline content={item.type.headline} />
              </ContentBlockRow>
            );
          case 'text':
            return (
              <ContentBlockRow key={`text-${streamPosition}`}>
                <StoryText content={item.type.text} />
              </ContentBlockRow>
            );
          case 'imageTile':
            return (
              <ContentBlockRow key={`imageTile-${streamPosition}`}>
                <StoryImageTile
                  content={item.type.imageTile}
                  streamPosition={streamPosition}
                />
              </ContentBlockRow>
            );
          case 'videoTile':
            return (
              <ContentBlockRow key={`videoTile-${streamPosition}`}>
                <StoryVideoTile
                  content={item.type.videoTile}
                  streamPosition={streamPosition}
                />
              </ContentBlockRow>
            );
          case 'doubleTile':
            return (
              <ContentBlockRow key={`doubleTile-${streamPosition}`}>
                <StoryDoubleTile
                  content={item.type.doubleTile}
                  streamPosition={streamPosition}
                />
              </ContentBlockRow>
            );
          case 'imageText':
            return (
              <ContentBlockRow key={`imageText-${streamPosition}`}>
                <StoryImageText
                  content={item.type.imageText}
                  streamPosition={streamPosition}
                />
              </ContentBlockRow>
            );
          case 'outfit':
            return (
              <ContentBlockRow key={`outfit-${streamPosition}`}>
                <StoryOutfit
                  content={item.type.outfit}
                  streamPosition={streamPosition}
                  onClickAddCta={onClickAddCta}
                  addCtaLabel={addCtaTitle}
                />
              </ContentBlockRow>
            );
          case 'productRow': {
            const {sectionTracker} = item.type.productRow;

            return item.type.productRow.products.map(productTile => {
              const productId = productTile.productId;

              return (
                <StreamProductTileWrapper
                  key={`productRow-${productId}`}
                  productId={productId}
                  shallowRenderingState={shallowRenderingState}
                  renderTile={({isInViewOnRemount, tileRef}) => (
                    <ProductTileComponent
                      isInViewOnRemount={isInViewOnRemount}
                      tileRef={
                        tileRef as React.RefObject<HTMLDivElement | null>
                      }
                      tile={productTile}
                      streamPosition={streamPosition}
                      gridColumns={gridColumns}
                      addCtaLabel={addCtaTitle}
                      trackingData={{recoType: sectionTracker?.recoType}}
                      onClickAddCta={
                        onClickAddCta
                          ? handleClickAddCta({
                              productId,
                              filteredSizeId: productTile.selectedSizeId,
                              couponPromotionId: productTile.couponPromotionId,
                              recoType: sectionTracker?.recoType,
                              streamPosition,
                              locationId: sectionTracker?.recoType,
                            })
                          : undefined
                      }
                    />
                  )}
                />
              );
            });
          }
          case 'ctaButton':
            return (
              <ContentBlockRow key={`ctaButton-${streamPosition}`}>
                <StoryCtaButton
                  content={item.type.ctaButton}
                  streamPosition={streamPosition}
                />
              </ContentBlockRow>
            );
          default:
            return null;
        }
      })}
    </>
  );
};

const ContentBlockRow = styled.li`
  grid-column: 1 / -1;

  /* Spacing lives on the content-block row itself, never on product tiles -
     a product row can hold several tile <li>s sharing one grid row, and
     margin-top on only the first (adjacent) one misaligns it from its
     row-mates. This row always spans the full grid width, so its own
     margin pushes the whole next/previous row uniformly instead. */

  /* directly following a product tile */
  li:not(&) + & {
    margin-top: ${SPACING['Y.Xl2']};
  }

  /* directly followed by a product tile */
  &:has(+ li:not(&)) {
    margin-bottom: ${SPACING['Y.Xl2']};
  }
`;
